import { createContext, useCallback, useContext, useEffect, useState } from 'react';

/**
 * The prototype's `flash()` toast: bottom-centre pill on `ink`, gone after a
 * couple of seconds. Used for confirmations ("Link copied") and for actions
 * whose destination screen has not landed yet.
 */

interface ToastState {
  id: number;
  message: string;
}

const ToastContext = createContext<(message: string) => void>(() => undefined);

export function useToast(): (message: string) => void {
  return useContext(ToastContext);
}

export function ToastProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const [toast, setToast] = useState<ToastState | null>(null);

  const show = useCallback((message: string) => {
    setToast({ id: Date.now(), message });
  }, []);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(null), 2600);
    return () => window.clearTimeout(timer);
  }, [toast]);

  return (
    <ToastContext.Provider value={show}>
      {children}
      {toast ? (
        <div className="toast" role="status" key={toast.id}>
          {toast.message}
        </div>
      ) : null}
    </ToastContext.Provider>
  );
}
